import { useContext } from 'react';
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useNavigate } from 'react-router-dom';
import { ShopperContext } from '../context/shopper';

// Every 'min_max_note' edit box (OrderItem, OrderItemTable,
// AdminChangeOtherOrder, OthersOrders, OthersOrdersTable) PATCHes the same
// way, differing only in which orderItem id it targets. Call this once per
// component, then invoke the returned function with that id, e.g.:
//   handleMinMaxNoteClick(order.id)
//   handleMinMaxNoteClick(othd.othShopperOrderItem.id)
// to get the '(text) => void' callback that 'InputDynamicText' expects.
export const useOrderItemChangeHandlers = () => {
  const { changeOrderItem } = useContext(ShopperContext);

  const handleMinMaxNoteClick = (orderItemId) => (text) => {
    changeOrderItem({ min_max_note: text }, orderItemId);
  };

  return { handleMinMaxNoteClick };
};

export const useUtils = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const circle_tax_rate = ({
      circle_rate
    }) => {
      return (
        circle_rate == 0
          ? 0  // if circle_rate is set to 0, then 0 it is. Otherwise:
        //   : (circle_rate || '(not yet set)')
          : (circle_rate || '(' + t('circle.tax_rate.not_yet_set') + ')')
      );
    };

  // If shopper's circles are located in >1 country, return the countryCode
  const getCountryCodeIfGT1 = ({ shopper, countryCode, inString=false }) => {
    return (
      shopper.country_count > 1
        ? inString
            ? ` / ${countryCode}`
            : countryCode
        : ''
    )
  } 

  const get_shortnames_of_circle =({
      cShoppers, circle_id  // cShoppers is circleShoppers
    }) => {
      //console.log('gsoc params:', cShoppers, circle_id);
      const shortnameOfIdentifier =
        cShoppers.filter(cs => cs.circle_id == circle_id)
                 .map(cs => [cs.shopper.identifier, cs.shopper.shortname]);

      return Object.fromEntries(shortnameOfIdentifier);  // converts map to obj
    } 

  const is_shopper_a_circle_leader =({
      circles, shopper_id
    }) => {
      console.log('isacl params:', circles, shopper_id);
      return circles.find(c => {
        return c.leader_id == shopper_id
      });
    } 

  const is_shopper_leader_of_this_circle = ({
      circle, shopper_id
    }) => {
      return circle.leader_id === shopper_id
    } 

  const shopper_is_leader_of_these_circles = ({
      circles, shopper_id
    }) => {
      console.log('silotc params:', circles, shopper_id);
      return circles.filter(c => {
        return c.leader_id == shopper_id
      });
    } 

  const shopper_is_admin_of_these_circles = ({
      cShoppers, shopper_id  // cShoppers is circleShoppers
    }) => {
      return cShoppers.filter(cs => {
        return cs.shopper_id === shopper_id  &&  cs.shopper_is_admin
      });
    } 

  const is_shopper_a_circle_admin =({
      cShoppers, circle_id, shopper_id  // cShoppers is circleShoppers
    }) => {
      console.log('isaca params:', cShoppers, circle_id, shopper_id);
      return cShoppers.find(cs => {
        return cs.circle_id == circle_id  &&  cs.shopper_id == shopper_id  &&  cs.shopper_is_admin
      });
    } 

  const is_shopper_admin_of_some_circle =({
      cShoppers, shopper_id  // cShoppers is circleShoppers
    }) => {
      return cShoppers.find(cs => {
        return cs.shopper_id == shopper_id  &&  cs.shopper_is_admin
      });
    } 

  const shoppers_order_for_this_sku_this_trip =({
      oItems, shopper_id, sku_id, trip_id
    }) => {
      return oItems.find(item =>
        item.trip.id == trip_id
        && item.sku.id == sku_id
        && item.shopper.id == shopper_id
      );
    }

  const isEmptyUnpaid = ({ circle }) => {
    return  (circle.shopper_count === 1)  &&  ! circle.is_current
  }

  const getSpinString = ({place}) => {
    switch (place % 3) {
      case 0:
        return '*__';
      case 1:
        return '_*_';
      case 2:
        return '__*'
      default:
        return '_*_';
    }
  };
 
  // can find time in seconds for a date string (dObj) or else for 'now'
  const getTimeInSeconds = (dObj) => {
    const dateObj =  dObj === undefined  ?  new Date()  :  new Date(dObj);
    return Math.round(dateObj.getTime() / 1000);
  };

  const randomChar = ({chars}) => {
    return chars.charAt(Math.floor(Math.random() * chars.length));
  };

  // 'stringGen' was removed 260821 along with the
  // 'global.identifierCharset*' translation keys that fed it. It built
  // a random identifier from an alphabet with 'o' and '0' left out,
  // since those two are hard to tell apart on screen. Nothing had
  // called it for some time. 'randomChar' above is left in place; it
  // was 'stringGen' that made it worth having, so it is now a candidate
  // for removal too.

  // sort routine from
  // https://www.delftstack.com/howto/javascript/sort-array-based-on-some-property-javascript/
  // TODO: could be extended to sort by value of an attribute parameter
  function s_by_identifier( a, b )
    {
      if ( a.identifier.toLowerCase() < b.identifier.toLowerCase()){
        return -1;
      }
      if ( a.identifier.toLowerCase() > b.identifier.toLowerCase()){
        return 1;
      }
      return 0;
    }

  function s_by_identifier_xip_code( a, b )
    {
      if ( a.identifier.toLowerCase() < b.identifier.toLowerCase()){
        return -1;
      }
      if ( a.identifier.toLowerCase() > b.identifier.toLowerCase()){
        return 1;
      }
      if ( a.xip_code < b.xip_code){
        return -1;
      }
      if ( a.xip_code > b.xip_code){
        return 1;
      }
      return 0;
    }

  function s_by_updated_at( a, b )
    {
      if ( a[0].updated_at < b[0].updated_at ){
        return -1;
      }
      if ( a[0].updated_at > b[0].updated_at ){
        return 1;
      }
      return 0;
    }

  const any_identifiers_in_multiple_postal_codes = ({circles}) => {
    // TODO: Find a better way to determine if any of the
    // shopper's circles share the same identifier. This can happen if
    // they are members of shopping circles in different postal codes
    // that have the same identifier.

    //console.log('setmyst circles', circles)
    // Create a unique set of identifier/postal_code strings ("cblobs")
    // Assumes all circles have the same value country, i.e. 'US'
    const cblobs = new Set(
      circles.map( c => "".concat(c.identifier, '/', c.postal_code) )
    );
    //console.log('setmyst cblobs', cblobs.keys())

    // Create a set of the various identifier terms of the cblobs
    const identifiers = new Set(
      Array.from(cblobs).map( blob => blob.split('/')[0] )
    )
    //console.log('setmyst identifiers', identifiers.keys())

    // If the count of identifiers is less than the count of blobs then
    // some of the identifiers must have been found in multiple postal_codes
    return cblobs.size > identifiers.size
  }

  function s_by_created_at( a, b )
    {
      if ( a.created_at < b.created_at ){
          return -1;
      }
      if ( a.created_at > b.created_at ){
        return 1;
      }
      return 0;
    }

  const toMmmDdYyyy = ({
      dateString,  // e.g. "2020-04-03"
      // The fact that the application's API may have already mapped this date
      // from the utc-based date in the database to the date appropriate for the
      // circle's time zone is invisible to the browser.
      // Since a time zone is not part of the received string, the string will
      // be treated as a utc-based date.
      weekday,
    }) => {
      if (dateString) {
        const dateObj = new Date(dateString);
        // console.log("toMmmDdYyyy in", dateObj)
        //console.log("toMmmDdYyyy in", dateObj.toString())
        
        const options = {
          weekday: weekday,
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "utc",
          // 'utc' is needed to fix output zone to same as input zone ('utc').
          // Else it thinks date to show is based on the local zone, e.g.
          // the time zone of the shopper's browser, which might be
          // "America/New_York", which is 4-5 hours behind utc, 8 PM yesterday,
          // which causes showing yesterday's date.
        };
      
        const newDateString =
          dateObj.toLocaleDateString(i18n.resolvedLanguage, options);
        // Was 'undefined' here, which means 'whatever locale the browser
        // is set to' - so a shopper reading the es pages still got
        // 'Sun, Aug 23, 2026' instead of 'dom, 23 ago 2026'. Ask for the
        // language the page is actually in (fixed 260823).
        //
        // Caveat: ICU has no Haitian Creole date data, so 'ht' resolves
        // to en-US and those dates stay English. Localizing them means
        // formatting from our own month/weekday keys rather than 'Intl'.
        //const newDateString = dateObj.toLocaleDateString('fr', options);  // looks French!
        //console.log("toMmmDdYyyy out", newDateString);
        return newDateString;
        // e.g. return dateObj.toLocaleDateString("en-US", options);
      } else {

        return '';
      }
    };

  const toHhColonMm = ({
      timeString,  // e.g. "1400", "0900", "900"
    }) => {
      const str = '0' + timeString.toString();

      return  str.slice(-4, -2) + ":" + str.slice(-2);
    };

  const toHmmA = ({
      timeString,  // e.g. "1400", "0900", "900"
    }) => {
      console.log('uutimeString', timeString);
      // Not sure why 'timeString' isn't respecting string methods, so do:
      const str = timeString.toString();

      return DateTime.fromFormat(str, 'Hmm')
                     .toFormat('h:mm a');
      // TODO: would be nice:
      //             .setLocale(t('global.locale'))
      //             // e.g. .setLocale('en')
      //             .toLocaleString('h:mm a');
      //             // e.g. 2:00 PM
    };

  // If we copy the 'GoShopping' list info directly into the Clipboard via
  // 'tableRef.current?.innerText', the utility emits a newline char when it
  // digests the button that dims. (The normal UI does not show a "\n" effect.)
  // This splits the text of product quantity, label, price, etc., over two
  // lines, so it would help to remove the "\n" from the text before sending it
  // to the clipboard. So, 'transformShopListText':
  // - removes that "\n" character,
  // - inserts '*' chars in front of each category title, and then
  // - deletes the errant '*' char that slips in before the list's
  //   date/time/label header.
  // Using regexes for cleanup is brittle: The layout of the GoShopping
  // list might change over time. Maybe we should clip its text based on
  // '{[goShopList.flat(1)]}' rather than what the DOM puts on the screen.
  // Note: "\u00a0" is Unicode for: &nbsp;
  const transformShopListText = (text) => {
    const result = text
      .replace(/\}[\u00A0]*\n/g, '}: ') // look for brace, &nbsp; chars, and \n
      .replace(/^(\w)/mg, "* $1") // ignores 'lines' seeming indented via &nbsp;
      .replace(/^[*] /, "") // first line also got a '*', so remove it. (No /mg)

    return result;
  }

  // TODO: Evaluate if 'useNbsp' will ever come in as 'false' - else remove it?
  const getTripDateTimeLabel = ({trip, useNbsp=true}) => {
    const spaceOrNbsp =  useNbsp  ? "\u00A0"  : ' ';
    return (
      toMmmDdYyyy({dateString: trip.day, weekday: 'short'}) + ',' +
      spaceOrNbsp + toHmmA({timeString: trip.hour}) + ',' +
      spaceOrNbsp + trip.label
    )
  };

  const identifierFitsUpcRegex = /^[ ]?[0-9]{12,13}[ ]?$/;

  const convertTo12DigitIfEAN13code = (code, wants12digitEAN) => {
    return (
      identifierFitsUpcRegex.test(code)
      && code.length === 13
      && wants12digitEAN
      && code.startsWith('0')
    )
      ? code.substring(1)
      : code || '';
  };

  const fetchFoodFactInfo = async ({
    barcode,
    fnSetFoodFactsLoading,
    fnSetFoodFactInfo,
  }) => {
    fnSetFoodFactsLoading(true);
    const upcValue = barcode.replace(/[ \D]/g, '');

    try {
      const response = await fetch(`/api/food_facts/${upcValue}`);
      
      // Check if response is actually JSON before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.log("Server returned a non-JSON response (likely a crash).");
        throw new Error("Server returned a non-JSON response (likely a crash).");
      }

      const data = await response.json();
      
      if (response.ok) {
        // console.log('ss ffi', data);
        fnSetFoodFactInfo(data);
      } else {
        console.log('ss effi', data);
        alert(data.errors.at(-1) || t('barcode.lookup.unknownError'));
        fnSetFoodFactInfo(null);
      }
    } catch (error) {
      console.error("Error fetching Open Food Fact info:", error);
      alert(t('barcode.lookup.noProductUponLookup', { code: upcValue }));
      // Lookup service could not find info about 'upcValue'"
    } finally {
      fnSetFoodFactsLoading(false);
    }
  };

  const goBack = ({e}) => {
    navigate(-1);
  }

  const checkForParamAndNoParamState = ({stateRes, param_id}) => {
    // check if we were passed a parameter, e.g. a 'circle_id', but also an
    // empty state resource such that param_id/resource lookup will fail. e.g.,
    // check if we have a circle parameter but also an empty 'circles' array:
    return (param_id  &&  param_id.length > 0  &&  stateRes.length === 0)
    // If we have a 'param_id' such as 'circle_id' then we likely first got
    // here by clicking a url that knew to look for that specific id resource
    // inside of some state collection. Examples would include clicking
    // 'Circle's Skus' or 'Circle's Trips' on a circle page. Or wanting to add
    // a price for a specific Sku as found in state of 'skus'.
    // If user then uses browser to refresh the page, they lose access to state.
    // Not having any state means that the state resource array, such as
    // 'circles', is empty and cannot provide access for the lookup by id.
  }

  // hardcode '/100' is ok for now. Only Stripe-based routines use this functon.
  const formatCentsToDollars = ({
      cents
    }) => {
      // Input validation
      if (typeof cents !== 'number' || !Number.isInteger(cents)) {
        throw new Error('Input must be an integer representing cents.');
      }

      // Convert cents to dollars
      const dollars = cents / 100;

      // Use Intl.NumberFormat for robust currency formatting
      // 'en-US' for United States locale |'currency' style |'USD' for US Dollar
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2, // Ensure two decimal places
        maximumFractionDigits: 2  // Ensure two decimal places
      });

      return formatter.format(dollars);
    }

// Examples:
// console.log(formatCentsToDollars(12345));  // Output: $123.45
// console.log(formatCentsToDollars(99));     // Output: $0.99
// console.log(formatCentsToDollars(1234567)); // Output: $12,345.67
// console.log(formatCentsToDollars(0));      // Output: $0.00
// console.log(formatCentsToDollars(-500));   // Output: -$5.00 (handles negative numbers)

  return {
    toHhColonMm,
    toHmmA,
    toMmmDdYyyy,
    formatCentsToDollars,
    isEmptyUnpaid,
    getSpinString,
    getTimeInSeconds,
    getTripDateTimeLabel,
    transformShopListText,
    identifierFitsUpcRegex,
    convertTo12DigitIfEAN13code,
    fetchFoodFactInfo,
    goBack,
    checkForParamAndNoParamState,
    randomChar,
    s_by_created_at,
    s_by_identifier,
    s_by_identifier_xip_code,
    s_by_updated_at,
    any_identifiers_in_multiple_postal_codes,
    getCountryCodeIfGT1,
    get_shortnames_of_circle,
    is_shopper_a_circle_admin,
    is_shopper_a_circle_leader,
    is_shopper_leader_of_this_circle,
    shopper_is_leader_of_these_circles,
    shopper_is_admin_of_these_circles,
    is_shopper_admin_of_some_circle,
    shoppers_order_for_this_sku_this_trip,
    circle_tax_rate
  }
}